import { useEffect, useState } from "react";
import { api } from "../../api";
import { Card, Badge, Avatar, PageHead, StatTile } from "../../components/ui";
import { BRAND, BAR_COLORS } from "../../theme";

function BarChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-2 sm:gap-3 h-44 pt-4 px-1">
      {data.map((d, i) => (
        <div key={d.label} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
          <div className="text-xs font-bold text-slate-600 tabular-nums">{d.value}</div>
          <div className="w-full max-w-[40px] rounded-lg transition-all duration-500 ease-out"
            style={{
              height: Math.max(8, (d.value / max) * 130) + "px",
              backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
              opacity: 0.85,
            }} />
          <div className="text-[10px] text-slate-500 text-center leading-tight h-8 overflow-hidden truncate w-full px-0.5">{d.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function Overview() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    api.students().then(setStudents).catch(() => {});
    api.courses().then(setCourses).catch(() => {});
  }, []);

  const inCourse = (name) => students.filter((s) => s.course_name === name);
  const count = (list, st) => list.filter((s) => s.status === st).length;
  const chart = courses.map((c) => ({ label: c.name.split(" ").slice(0, 2).join(" "), value: inCourse(c.name).length }));

  return (
    <div>
      <PageHead title="Overview" sub="Enrolment health across every active course." />

      {/* Stat tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatTile label="Total enrolments" value={students.length} icon="users" i={0} />
        <StatTile label="Active learners" value={students.filter((s) => s.status === "Active" && s.is_active).length} icon="zap" i={1} />
        <StatTile label="Completed" value={students.filter((s) => s.completed).length} icon="award" i={2} />
        <StatTile label="Courses live" value={courses.length} icon="book" i={3} />
      </div>

      {/* Chart + Recent enrolments */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 lg:col-span-2">
          <div className="font-semibold text-slate-800 mb-1">Enrolments by course</div>
          <div className="text-xs text-slate-400 mb-4">Distribution across all active courses</div>
          {chart.length ? <BarChart data={chart} /> : <div className="text-sm text-slate-400 py-12 text-center">No data yet.</div>}
        </Card>
        <Card className="p-6">
          <div className="font-semibold text-slate-800 mb-1">Recent enrolments</div>
          <div className="text-xs text-slate-400 mb-4">Latest students added</div>
          <div className="space-y-4">
            {students.slice(-5).reverse().map((s) => (
              <div key={s.id} className="flex items-center gap-3">
                <Avatar name={s.full_name} size={34} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{s.full_name}</div>
                  <div className="text-xs text-slate-400 truncate">{s.course_name}</div>
                </div>
              </div>
            ))}
            {students.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No students yet.</div>}
          </div>
        </Card>
      </div>

      {/* Course breakdown cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {courses.map((c) => {
          const list = inCourse(c.name);
          const cells = [
            ["Enrolled", list.length],
            ["Active", count(list, "Active")],
            ["Completed", count(list, "Completed")],
            ["On hold", count(list, "On Hold")],
            ["Left", count(list, "Discontinued")],
          ];
          return (
            <Card key={c.id} className="p-5 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">{c.name}</h3>
                <Badge>{c.ctype}</Badge>
              </div>
              <div className="grid grid-cols-5 gap-2 text-center">
                {cells.map(([l, n]) => (
                  <div key={l}>
                    <div className="text-xl font-bold tabular-nums" style={{ color: BRAND }}>{n}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
